import React, { useMemo } from 'react';
import { isNil } from 'lodash';
import { EclipseSvg, BothSvg, GeoFeatureSvg, SunlitSvg, TargetTrackSvg, ClockSvg } from '../../../Missions/Satellites/FaultInjection/FaultPlanSvgs';
import type { MappedFault } from '../utils/faultMappingUtils';
import { formatSecondsDuration } from '../../../../utils/commonFunctions';
import { FaultSchedulingTooltipChip } from './FaultSchedulingTooltipChip';

const GeofeatureType = {
  GEOFENCE: "Geofence",
  TARGET_TRACK: "TargetTrack"
} as const;

const SunExposure = {
  ECLIPSE: "Eclipse",
  BOTH: "Both",
  SUNLIT: "Sunlit"
} as const;

interface FaultSchedulingChipsProps {
  fault?: MappedFault;
}

const formatExecDurationValue = (value: unknown): string | null => {
  if (isNil(value)) return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(numeric)) {
    if (numeric === 0) return null;
    return `${numeric}min`;
  }
  return String(value);
};

export const FaultSchedulingChips: React.FC<FaultSchedulingChipsProps> = ({ fault }) => {
  const execReq = fault?.executionRequirement;

  // Memoize computed values to prevent unnecessary re-renders
  const timeOffsetText = useMemo(() => {
    if (isNil(execReq?.timeOffset)) return null;
    return `T₀ + ${execReq.timeOffset} min`;
  }, [execReq?.timeOffset]);

  const execDurationText = useMemo(() => formatExecDurationValue(execReq?.duration), [execReq?.duration]);
  const faultDurationText = useMemo(
    () => formatSecondsDuration(fault?.duration, { hideZero: true }),
    [fault?.duration]
  );
  const durationsMatch = useMemo(
    () => Boolean(execDurationText && faultDurationText && execDurationText === faultDurationText),
    [execDurationText, faultDurationText]
  );

  const durationTooltipText = useMemo(() => {
    if (durationsMatch && execDurationText) {
      return execDurationText;
    }
    if (execDurationText && faultDurationText) {
      return `${execDurationText} → ${faultDurationText}`;
    }
    return faultDurationText ?? execDurationText ?? null;
  }, [durationsMatch, execDurationText, faultDurationText]);

  const durationTooltip = useMemo(() => {
    if (!durationTooltipText) return null;
    return `Duration: ${durationTooltipText}`;
  }, [durationTooltipText]);
  return (
    <>
      {/* Geo-Feature */}
      {execReq?.geofeatureName && (
        <FaultSchedulingTooltipChip title={execReq.geofeatureName}>
          <div className="fi-chip" aria-label={`Geofeature: ${execReq.geofeatureName}`}>
            {execReq.geofeatureType === GeofeatureType.GEOFENCE ? (
              <GeoFeatureSvg />
            ) : (
              <TargetTrackSvg />
            )}
            <span className="fi-chip-text">
              {execReq?.geofeatureName}
            </span>
          </div>
        </FaultSchedulingTooltipChip>
      )}

      {/* Sun Exposure */}
      {execReq?.sunExposure && (
        <FaultSchedulingTooltipChip title={execReq.sunExposure}>
          <div className="fi-chip" aria-label={`Sun Exposure: ${execReq.sunExposure}`}>
            {execReq.sunExposure === SunExposure.ECLIPSE ? (
              <EclipseSvg />
            ) : execReq.sunExposure === SunExposure.BOTH ? (
              <BothSvg />
            ) : (
              <SunlitSvg />
            )}
          </div>
        </FaultSchedulingTooltipChip>
      )}

      {/* Time Offset */}
      {timeOffsetText && (
        <FaultSchedulingTooltipChip title={timeOffsetText}>
          <div className="fi-chip" aria-label={timeOffsetText}>
            {timeOffsetText}
          </div>
        </FaultSchedulingTooltipChip>
      )}

      {/* Duration */}
      {durationTooltip && (
        <FaultSchedulingTooltipChip title={durationTooltip}>
          <div className="fi-chip" aria-label={durationTooltip}>
            <ClockSvg />
            {(execDurationText && faultDurationText && !durationsMatch) ? (
              <>
                <span className="fi-chip-duration-original">{execDurationText}</span>
                <span className="fi-chip-duration-updated">{faultDurationText}</span>
              </>
            ) : (
              <span>{faultDurationText ?? durationTooltipText}</span>
            )}
          </div>
        </FaultSchedulingTooltipChip>
      )}
    </>
  );
};
